import * as React from 'react';
import styled from 'styled-components';

import { palette } from 'theme/variables';

/* ══════════════════════════════════════════════════════
   Status Pill — rounded chip with coloured dot
   Matches design brief Option B v4
   ══════════════════════════════════════════════════════ */

type StatusTone = 'success' | 'progress' | 'neutral' | 'danger' | 'info';

const TONE_MAP: Record<StatusTone, { bg: string; color: string; dot: string }> = {
    success: { bg: palette.sageLt, color: '#0D5C2E', dot: palette.forest },
    progress: { bg: palette.goldLt, color: '#7A5F00', dot: palette.gold },
    neutral: { bg: palette.warm100, color: palette.warm400, dot: palette.warm400 },
    danger: { bg: '#FBEAEA', color: '#7D1F1F', dot: palette.ragRed },
    info: { bg: '#DBEAFE', color: '#2A5680', dot: '#2A7FAB' },
};

const STATUS_TONES: Record<string, StatusTone> = {
    DELIVERED: 'success',
    COMPLETED: 'success',
    AWARDED: 'success',
    IN_PROGRESS: 'progress',
    IN_DEVELOPMENT: 'progress',
    ON_HOLD: 'progress',
    TENDERED: 'progress',
    PARTIALLY_DELIVERED: 'progress',
    AWAITING_THRESHOLD: 'info',
    AWAITING_REVIEW: 'info',
    NOT_STARTED: 'neutral',
    SUBSTITUTED: 'neutral',
    INCOMPLETE: 'neutral',
    DELAYED: 'danger',
    NOT_DELIVERED: 'danger',
    OVERDUE: 'danger',
    ABANDONED: 'danger',
};

const STATUS_LABELS: Record<string, string> = {
    IN_PROGRESS: 'In Progress',
    DELIVERED: 'Delivered',
    NOT_STARTED: 'Not Started',
    COMPLETED: 'Completed',
    IN_DEVELOPMENT: 'In Development',
    DELAYED: 'Delayed',
    NOT_DELIVERED: 'Not Delivered',
    OVERDUE: 'Overdue',
    ABANDONED: 'Abandoned',
    PARTIALLY_DELIVERED: 'Partially Delivered',
    SUBSTITUTED: 'Substituted',
    INCOMPLETE: 'Incomplete',
    ON_HOLD: 'On Hold',
    TENDERED: 'Tendered',
    AWARDED: 'Awarded',
    AWAITING_THRESHOLD: 'Awaiting Threshold',
    AWAITING_REVIEW: 'Awaiting Review',
};

const Pill = styled.span<{ tone: StatusTone }>`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    line-height: 1.4;
    white-space: nowrap;
    background: ${(p) => TONE_MAP[p.tone].bg};
    color: ${(p) => TONE_MAP[p.tone].color};
`;

const Dot = styled.span<{ tone: StatusTone }>`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${(p) => TONE_MAP[p.tone].dot};
`;

interface Props {
    status: string;
}

export const StatusPill: React.FC<Props> = ({ status }) => {
    if (!status) {
        return null;
    }
    const tone: StatusTone = STATUS_TONES[status] || 'neutral';
    const label = STATUS_LABELS[status] || status;

    return (
        <Pill tone={tone}>
            <Dot tone={tone} />
            {label}
        </Pill>
    );
};

/* ── Category Pill — coloured chip (no dot) ───────────── */

const CATEGORY_TONE_MAP: Record<string, { bg: string; color: string }> = {
    employment: { bg: palette.sageLt, color: '#0D5C2E' },
    training: { bg: '#EDE9FE', color: '#4C3680' },
    biodiversity: { bg: palette.sageLt, color: '#0D5C2E' },
    decarbonisation: { bg: '#C2EDE9', color: '#0D6E62' },
    'supply chain': { bg: palette.goldLt, color: '#7A5F00' },
    wellbeing: { bg: '#FFE4D8', color: '#7D1F1F' },
    education: { bg: '#EDE9FE', color: '#4C3680' },
    infrastructure: { bg: '#DBEAFE', color: '#2A5680' },
    environmental: { bg: '#C2EDE9', color: '#0D6E62' },
    volunteering: { bg: '#FFE4D8', color: '#7D1F1F' },
};

const CategoryChip = styled.span<{ tone: { bg: string; color: string } }>`
    display: inline-block;
    padding: 2px 9px;
    border-radius: 20px;
    font-size: 11px;
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    line-height: 1.5;
    white-space: nowrap;
    background: ${(p) => p.tone.bg};
    color: ${(p) => p.tone.color};
`;

interface CategoryProps {
    category?: string;
}

export const CategoryPill: React.FC<CategoryProps> = ({ category }) => {
    if (!category) {
        return null;
    }
    const key = category.toLowerCase();
    const tone = Object.entries(CATEGORY_TONE_MAP).find(([k]) => key.includes(k))?.[1] || {
        bg: palette.warm100,
        color: palette.warm700,
    };

    return <CategoryChip tone={tone}>{category}</CategoryChip>;
};
